<script lang="ts">
	import Layout from "../components/Layout.svelte";
	import { getGlobalSettings } from "../getGlobalSettings.js";
	import Link from "../components/Link.svelte";
	import preloader from "../../../core/assets/icons/preloader.svg?raw";
	import cake from "../../../core/assets/icons/cake.svg?raw";
	import { nbspify } from "../../../core/utils/nbspify.js";
	import LabelledInput from "../components/Form/LabelledInput.svelte";
	import Button from "../components/Button.svelte";
	import Form from "../components/Form/Form.svelte";
	import { createPromiseStore } from "../../../core/utils/createPromiseStore.js";
	import { getClients } from "../appsync/getClients.js";
	import { expectErrorMessage } from "../components/Form/expectErrorMessage.js";
	import type { Customer } from "../../../core/schema/Customer.js";
	import getCustomer from "../appsync/queries/getCustomer.graphql";
	import { loggedCustomerStore } from "../stores/loggedCustomerStore.js";
	import { myAccountPathMap } from "../../../core/schema/paths/myAccountPathMap.js";
	import { forgotPasswordPathMap } from "../../../core/schema/paths/forgotPasswordPathMap.js";
	import Icon from "../components/Icon.svelte";
	import Message from "../components/Message.svelte";
	import Infobox from "../components/Infobox.svelte";
	import Successbox from "../components/Successbox.svelte";

	const globalSettings = getGlobalSettings();
	const { TENANT_LOCALE } = globalSettings;

	const urlSearchParams = new URLSearchParams(typeof window !== "undefined" ? window.location.search : "");
	const email = decodeURIComponent(urlSearchParams.get("email") ?? "");
	const confirmed = decodeURIComponent(urlSearchParams.get("confirmed") ?? "");

	const signIn = createPromiseStore(async (username: string, password: string) => {
		const { cognito } = await getClients(globalSettings);
		await cognito.login(username, password);
		const { graphQL } = await getClients(globalSettings); // Gets updated GraphQL client with logged in information.
		return await graphQL.query<Customer>({ query: getCustomer });
	});

	$: signInErrorMessage =
		$signIn.status === "error" &&
		expectErrorMessage(
			$signIn.error,
			TENANT_LOCALE,
			{
				NotAuthorizedException: "Neplatné přihlašovací údaje.",
			},
			"Chyba při přihlášení. Zkuste to prosím znovu nebo později.",
		);

	let passwordInput: HTMLInputElement | null = null;

	async function submit(event: SubmitEvent): Promise<void> {
		if ($signIn.status === "loading") {
			return;
		}
		const form = event.target as HTMLFormElement;
		const formData = new FormData(form);
		try {
			const loggedCustomer = await signIn.call(String(formData.get("email")), String(formData.get("password")));
			if (loggedCustomer) {
				loggedCustomerStore.set(loggedCustomer);
				window.location.href = `/${myAccountPathMap[TENANT_LOCALE]}`;
			}
		} finally {
			if (passwordInput) {
				passwordInput.value = "";
			}
		}
	}
</script>

<Layout>
	<div class="container flex max-w-[40.5rem] flex-col gap-8">
		<div class="mt-5 flex flex-col rounded-[22px] border-2 border-gray-200 px-6 py-12">
			<h1 class="text-md pt-0 font-mono uppercase tracking-[.95rem]">{nbspify("Přihlášení")}</h1>
			<h2 class="mb-10 text-center text-[3.5rem]">{nbspify("Můj sladký účet")}</h2>

			<Form onsubmit={submit} class="flex flex-col gap-2 md:px-5">
				<LabelledInput id="email" value={email} type="email" required autocomplete="username">E-mail</LabelledInput>
				<LabelledInput
					bind:input={passwordInput}
					id="password"
					type="password"
					required
					autocomplete="current-password"
					inputProps={{ minlength: 7 }}
					class="flex flex-col !gap-1"
				>
					Heslo
				</LabelledInput>
				<div class="flex w-full flex-col items-center gap-3">
					{#if $signIn.status === "loading"}
						<Icon icon={preloader} class="aspect-auto h-10 w-12" />
					{:else}
						<Button type="submit" class="mt-3 w-full py-4 text-[1.375rem]" icon={cake} center iconClass="w-6 h-6">
							Přihlásit se
						</Button>
					{/if}
					<Link href="/{forgotPasswordPathMap[TENANT_LOCALE]}">Zapomněl(a) jste heslo?</Link>
				</div>
			</Form>
			<!-- TODO Odkomentovat jakmile bude funkční stránka pro registraci -->
			<!-- <div class="mb-8 mt-10 flex items-center gap-2">
				<div class="flex-grow border-t border-gray-300"></div>
				<span class="text-sm text-gray-300">NEBO</span>
				<div class="flex-grow border-t border-gray-300"></div>
			</div>
			<span class="text-center text-[1.15rem]"
				>Nemáte účet ? <Link href="/{signUpPathMap[TENANT_LOCALE]}" class="font-semibold text-red-600"
					>Zaregistrujte se</Link
				></span
			> -->
		</div>

		{#if signInErrorMessage}
			<Message variant="error">
				{nbspify(signInErrorMessage)}
			</Message>
		{/if}
		<!-- TODO Odkomentovat jakmile bude funkční stránka pro registraci -->
		<!-- <Infobox
			text={'Zaregistrovat se můžete i tak, že u objednávky zaškrtnete políčko "Chci založit uživatelský účet a využívat výhody".'}
		/> -->
		<!-- TODO Smazat jakmile bude dostupná registrace -->

		{#if confirmed === "true"}
			<Successbox>{nbspify("Registrace účtu byla úspěšně dokončena. Nyní se můžete přihlásit.")}</Successbox>
		{:else}
			<Infobox>
				{nbspify(
					'Zaregistrovat se můžete tak, že u objednávky zaškrtnete políčko "Chci založit uživatelský účet a využívat výhody".',
				)}
			</Infobox>
		{/if}
	</div>
</Layout>
